import { Button, Tag, Tooltip } from 'antd';
import { PhoneFilled } from '@ant-design/icons';
import { t } from '../i18n';

// flagcdn.com is a free, public flag CDN. The 16x12 size renders clean
// inside an inline tag without distorting. EN maps to the UK flag (gb)
// since flagcdn doesn't ship a generic "English language" flag.
const FLAG_BY_LANG: Record<string, { code: string; label: string }> = {
  UZ: { code: 'uz', label: "O'zbekcha" },
  RU: { code: 'ru', label: 'Русский' },
  EN: { code: 'gb', label: 'English' },
};

export const LanguageFlag = ({
  lang,
  size = 16,
}: {
  lang: string | null | undefined;
  size?: number;
}) => {
  if (!lang) return null;
  const meta = FLAG_BY_LANG[lang.toUpperCase()];
  if (!meta) return null;
  // 16x12 is the smallest flagcdn variant; 24x18 doubles it. We pass the
  // exact CSS size so retina displays don't blur. `loading="lazy"` keeps
  // off-screen rows from blocking the initial paint when the table is
  // long.
  const fileSize = size <= 16 ? '16x12' : '24x18';
  return (
    <Tooltip title={meta.label}>
      <img
        src={`https://flagcdn.com/${fileSize}/${meta.code}.png`}
        alt={meta.label}
        width={size}
        height={Math.round(size * 0.75)}
        loading="lazy"
        style={{
          display: 'inline-block',
          verticalAlign: 'middle',
          borderRadius: 2,
          // Subtle border so the flag stays visible on white card
          // backgrounds (UZ flag has a very light top stripe).
          boxShadow: '0 0 0 1px rgba(0, 0, 0, 0.08)',
        }}
      />
    </Tooltip>
  );
};

/**
 * The "call" action in list tables — bigger rounded-rectangle button instead
 * of a bare icon so it's actually visible/tappable. `color` tints the phone
 * glyph with the operator's call-colour tag (see resolveCallIconColor).
 */
export const CallButton = ({
  color,
  onClick,
}: {
  color: string;
  onClick: (e: React.MouseEvent) => void;
}) => (
  <Button
    type="text"
    onClick={onClick}
    style={{
      width: 48,
      height: 36,
      padding: 0,
      borderRadius: 8,
      background: 'rgba(0, 0, 0, 0.045)',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <PhoneFilled style={{ color, fontSize: 17 }} />
  </Button>
);

const ROLE_TAG_COLOR: Record<string, string> = {
  CLIENT: 'default',
  DRIVER: 'blue',
  ADMIN: 'purple',
};

const roleLabel = (role: string): string => {
  switch (role) {
    case 'DRIVER':
      return t('role.driver');
    case 'CLIENT':
      return t('role.client');
    case 'ADMIN':
      return 'Admin';
    default:
      return role;
  }
};

/**
 * Renders the role pair as two explicitly-labeled tags, always:
 *
 *   "Hozir: Yo'lovchi"   ← currentRole
 *   "Boshida: Haydovchi" ← initialRole (the role picked at signup)
 *
 * Both tags are always rendered, even when they're equal, so the
 * column is visually consistent across the whole table — operators
 * scanning the list don't have to wonder "did this row not switch
 * roles, or am I missing data?". When the two values match, the
 * pair just confirms "user signed up as Y and is still Y."
 *
 * If `initialRole` is null (older rows that pre-date the column and
 * were never backfilled), we render a muted "—" placeholder for the
 * second tag rather than hiding it.
 */
export const RolePair = ({
  currentRole,
  initialRole,
}: {
  currentRole: string;
  initialRole: string | null | undefined;
}) => {
  const initialTag = initialRole ? (
    <Tag
      color={ROLE_TAG_COLOR[initialRole] || 'default'}
      bordered
      style={{ margin: 0, fontSize: 11 }}
    >
      <span style={{ opacity: 0.65, marginRight: 4 }}>{t('users.roleInitial')}:</span>
      {roleLabel(initialRole)}
    </Tag>
  ) : (
    <Tag bordered style={{ margin: 0, fontSize: 11, opacity: 0.5 }}>
      <span style={{ opacity: 0.65, marginRight: 4 }}>{t('users.roleInitial')}:</span>—
    </Tag>
  );

  return (
    <span
      style={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 2,
      }}
    >
      <Tag color={ROLE_TAG_COLOR[currentRole] || 'default'} style={{ margin: 0 }}>
        <span style={{ opacity: 0.65, marginRight: 4 }}>{t('users.roleNow')}:</span>
        {roleLabel(currentRole)}
      </Tag>
      <Tooltip title={t('users.signedUpAs')}>{initialTag}</Tooltip>
    </span>
  );
};
